import { Link } from "react-router-dom";
import { BRAND, BrandMarquee } from "@/constants/brand";
import { AmbientVideo } from "@/components/media/VideoEmbed";
import { FlavorBar } from "@/components/media/FlavorWall";
import { Texture, TribalRule } from "@/components/media/Sections";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, CalendarDays, Hammer } from "lucide-react";

const Door = ({ to, testId, eyebrow, title, body, cta, icon, media, poster, image }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group relative flex min-h-[26rem] flex-col justify-end overflow-hidden border border-border bg-[#0d0d0d] p-7 transition-colors hover:border-primary md:min-h-[34rem] md:p-10"
  >
    {media ? (
      <AmbientVideo
        url={media}
        poster={poster}
        className="absolute inset-0 h-full w-full object-cover opacity-35 transition-opacity duration-700 group-hover:opacity-55"
      />
    ) : (
      <img
        src={image}
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-30 transition-all duration-700 group-hover:scale-105 group-hover:opacity-50"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
    <div className="relative">
      <div className="mono text-[11px] tracking-[0.35em] text-primary">{eyebrow}</div>
      <h2 className="display mt-3 text-4xl uppercase leading-[0.95] md:text-5xl">{title}</h2>
      <p className="mt-4 max-w-sm text-base text-muted-foreground">{body}</p>
      <span className="display mt-7 inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-lg uppercase text-primary-foreground transition-colors group-hover:bg-[#E6C800]">
        {icon}
        {cta}
        <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

/** The front door. Two audiences, two journeys, no confusion. */
export default function Choose() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background" data-testid="choose-page">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-4 md:px-10">
          <img src={BRAND.logo} alt="Smash Lemonade" className="h-10 w-auto" />
          <Link
            to={user ? "/academy-dashboard" : "/login"}
            data-testid="choose-login"
            className="mono ml-auto text-[11px] tracking-widest text-muted-foreground hover:text-primary"
          >
            {user ? "MY ACADEMY" : "LOG IN"}
          </Link>
        </div>
      </header>
      <FlavorBar testId="choose-flavor-bar" />

      <div className="tex">
        <Texture kind="tribal" />
        <div className="mx-auto max-w-6xl px-5 py-14 md:px-10 md:py-20">
        <h1 className="neon max-w-3xl text-4xl leading-[0.95] sm:text-5xl lg:text-6xl">
          When life gives you lemons,
          <br />
          <span className="text-primary">smash them.</span>
        </h1>
        <p className="mono mt-6 max-w-xl text-[11px] leading-relaxed tracking-widest text-muted-foreground">
          TWO WAYS IN. PICK THE ONE THAT SOUNDS LIKE YOU.
        </p>

        <div className="mt-12 grid gap-5 lg:grid-cols-2">
          <Door
            to="/events"
            testId="door-events"
            eyebrow="FOR CUSTOMERS"
            title={<>Book us for your event</>}
            body="Weddings, corporate days, school carnivals, festivals, birthdays. We roll in with the stand, the crew and fresh-smashed lemonade."
            cta="Book an event"
            icon={<CalendarDays size={17} />}
            media="/brand/event-atmosphere.mp4"
            poster="/brand/proof-crew-tent.jpg"
          />
          <Door
            to="/operators"
            testId="door-operators"
            eyebrow="FOR FUTURE OPERATORS"
            title={<>Build your own stand</>}
            body="Free lessons, real numbers and the stories behind Smash. Then the training and equipment to run your own drink business."
            cta="Explore the academy"
            icon={<Hammer size={17} />}
            image="/brand/founders-alvin-moana.jpg"
          />
        </div>
      </div>
      </div>

      <TribalRule testId="choose-tribal-rule" />

      <BrandMarquee />

      <footer className="px-5 py-10 md:px-10">
        <p className="mono mx-auto max-w-6xl text-[10px] leading-relaxed tracking-widest text-muted-foreground">
          SMASH LEMONADE SELLS LEMONADE. SMASH ACADEMY BUILDS OPERATORS.
        </p>
      </footer>
    </div>
  );
}
